<script setup lang="ts">
// @ts-nocheck
import findLast from 'lodash/findLast';
import first from 'lodash/first'
import orderBy from 'lodash/orderBy';
import {ArrowLeftRightIcon, CheckIcon, ChevronDownIcon, HeartIcon, MinusIcon, PlusIcon } from "lucide-vue-next";
import type {Ref} from "vue";
import {computed, onMounted, ref, watch} from "vue";
import {toast} from "vue-sonner";
import type {PriceResponse} from "../../../responses/PriceResponse";
import type {ProductResponse} from "../../../responses/ProductResponse";
import type {ProductVariantResponse} from "../../../responses/ProductVariantResponse";
import {Money} from "../../../utils/money";
import {label} from "../../mixins/translations.js";
import {useCartStore} from "../../stores/cart";
import {useProductStore} from "../../stores/product";
import CartStock from "../Cart/CartStock.vue";
import CustomNumberInput from "../CustomNumberInput.vue";
import AddToPurchaseList from "../PurchaseList/AddToPurchaseList.vue";
import FavoriteButton from "./FavoriteButton.vue";

type Events = {
    selectedVariantChanged: ProductVariantResponse
}

interface Props {
    product: ProductResponse,
    showInput: boolean
}

const props = defineProps<Props>();
const amount: Ref<number> = ref(1);
const cart = useCartStore();
const productStore = useProductStore();
const currentPrice: Ref<PriceResponse> = ref({});
const currentVariant: Ref<ProductVariantResponse> = ref(null);
const showPriceExcl: Ref<boolean> = ref(false);

const isConfigurator: Ref<boolean> = ref(false);

const addToCart = () => {
    cart.addItem({
        amount: amount.value,
        variant_id: productStore.selectedVariant.id
    }).then((_response) => {
        toast.success(label('site.product.popup.title'));
    });

    //todo reactivate when implementing google
    // window.trackAddToCartGoogle(props.product, amount.value, 'add_to_cart');
    reset();
}
const reset = () => {
    handleAmountChange(1);
}
const handleAmountChange = (newAmount) => {
    if(newAmount >= 1) {
        amount.value = newAmount;
        updatePrice();
    }
}

const updatePrice = () => {
    currentPrice.value = findLast(productStore.selectedVariant?.tieredPrices, (price: PriceResponse) => price.quantity <= amount.value)
    if (!currentPrice.value) {
        currentPrice.value = props.product.price;
    }
}

const vatLabel = computed(() => {
    return showPriceExcl.value ? label('site.order.excl_vat') : label('site.order.incl_vat')
})

const priceLabel = computed(() => {
    return showPriceExcl.value ? currentPrice.value?.price_excl : currentPrice.value?.price_incl;
})

const fromPriceLabel = computed(() => {
    return showPriceExcl.value ? currentPrice.value?.from_price_excl : currentPrice.value?.from_price_incl;
})


watch(
    () => currentVariant,
    () => {
        productStore.setSelectedVariant(currentVariant.value);
        updatePrice();
    },
    {deep: true}
)

onMounted(() => {
    currentVariant.value = first(orderBy(props.product.variants, 'price.price_incl'));
});
</script>
<template>
    <div class="mt-6 bg-secondary-100/70 p-5 md:p-6" v-if="currentPrice">
<!--        TODO: Locatie bepalen-->
        <CartStock v-if="currentVariant?.stock" :stock="currentVariant?.stock"></CartStock>
        <div v-if="!isConfigurator">
            <div class="grid gap-5 border-b border-secondary-200 pb-5 md:grid-cols-[1fr_auto] md:items-start">
                <div>
                    <span v-if="product.product_labels.length"
                                 class="relative w-fit -ml-5 lg:-ml-6 mb-4 z-10 inline-flex rounded-full rounded-l-none bg-primary px-4 py-0.5 text-base font-semibold text-white">
                        {{ first(product.product_labels).label }}
                    </span>
                    <div class="mt-2">
                        <div class="w-full flex items-end gap-2">
                            <span class="text-base font-semibold leading-none text-secondary-500 lg:text-lg line-through" v-if="currentPrice?.from_price_excl" v-text="Money.format(fromPriceLabel)"></span>
                            <span class="text-2xl font-semibold leading-none text-secondary-700 lg:text-4xl" v-text="Money.format(priceLabel)"></span>
                            <span class="pb-1 text-sm text-secondary-500" v-text="vatLabel"></span>
                        </div>
                    </div>
                </div>
                <div class="mt-0 mr-0 rounded-bl-2xl bg-transparent px-0 py-2 lg:-mt-6 lg:-mr-6 lg:bg-[#EBF4F1] lg:px-5">
                    <p class="text-sm font-bold text-secondary-700" v-text="label('site.product.show-price')"></p>
                    <div class="mt-2 flex items-center gap-3 text-sm text-secondary-600">
                        <span v-text="label('site.order.excl_vat')"></span>
                        <button type="button"
                                @click="showPriceExcl = !showPriceExcl"
                                class="relative inline-flex h-6 w-11 items-center rounded-full transition bg-secondary-300"
                                aria-label="Wissel btw weergave">
                            <span
                                class="inline-block h-5 w-5 transform rounded-full bg-white transition"
                                :class="!showPriceExcl ? 'translate-x-5' : 'translate-x-1'">
                            </span>
                        </button>
                        <span v-text="label('site.order.incl_vat')"></span>
                    </div>
                </div>
            </div>
            <div class="mt-6 flex flex-wrap items-center gap-4">
                <CustomNumberInput @input="handleAmountChange" :value="amount" :min="1"></CustomNumberInput>
                <button v-if="currentPrice" type="button" @click="addToCart()"
                        class="inline-flex items-center justify-center rounded-full bg-primary px-8 py-4 text-base font-semibold text-white transition hover:bg-primary-300">
                    {{ label('site.product.add_to_cart') }}
                </button>
                <FavoriteButton :product="product"></FavoriteButton>
                <AddToPurchaseList
                    v-if="productStore.selectedVariant"
                    :variant="productStore.selectedVariant"
                    class="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white border border-secondary-200/50 text-secondary-400 transition hover:text-secondary-800"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-icon lucide-list">
                        <title>add to purchase list</title>
                        <path d="M3 5h.01"/><path d="M3 12h.01"/><path d="M3 19h.01"/><path d="M8 5h13"/><path d="M8 12h13"/><path d="M8 19h13"/>
                    </svg>
                </AddToPurchaseList>
            </div>

        </div>
        <div v-else>
<!--            @TODO: Configurator view implementeren-->
            <div class="grid gap-5 border-b border-secondary-200 pb-5 md:grid-cols-[1fr_auto] md:items-start">
                <div>
                    <span v-if="product.product_labels.length"
                          class="relative w-fit -ml-5 lg:-ml-6 mb-4 z-10 inline-flex rounded-full rounded-l-none bg-primary px-4 py-0.5 text-base font-semibold text-white">
                        {{ first(product.product_labels).label }}
                    </span>
                    <p v-if="isConfigurator" class="text-sm font-bold text-secondary-700" v-text="label('site.product.total-excl-options')"></p>
                    <div v-if="isConfigurator"  class="mt-2">
                        <div class="w-full flex items-end gap-2">
                            <span class="text-base font-semibold leading-none text-secondary-500 lg:text-lg line-through" v-if="currentPrice?.from_price_excl" v-text="Money.format(fromPriceLabel)"></span>
                            <span class="text-2xl font-semibold leading-none text-secondary-700 lg:text-4xl" v-text="Money.format(priceLabel)"></span>
                            <span class="pb-1 text-sm text-secondary-500" v-text="vatLabel"></span>
                        </div>
                    </div>
                </div>
                <div class="mt-0 mr-0 rounded-bl-2xl bg-transparent px-0 py-2 lg:-mt-6 lg:-mr-6 lg:bg-[#EBF4F1] lg:px-5"><p
                    class="text-sm font-bold text-secondary-700" v-text="label('site.product.show-price')"></p>
                    <div class="mt-2 flex items-center gap-3 text-sm text-secondary-600"><span v-text="label('site.order.excl_vat')"></span>
                        <button type="button"
                                @click="toggleVat"
                                class="relative inline-flex h-6 w-11 items-center rounded-full transition bg-secondary-300"
                                aria-label="Wissel btw weergave"><span
                            class="inline-block h-5 w-5 transform rounded-full bg-white transition translate-x-5"></span>
                        </button>
                        <span v-text="label('site.order.incl_vat')"></span></div>
                </div>
            </div>
            <div>
                <div class="pt-3">
                    <div
                        class="flex flex-col gap-4 bg-white p-4 shadow-lg shadow-black/3 md:flex-row md:items-center md:justify-between">
                        <button type="button" class="flex items-center gap-4 text-left">
                            <span class="inline-flex h-7 w-7 items-center justify-center rounded-full text-white" style="background-color: rgb(42, 82, 147);">
                                <CheckIcon class="h-5 w-5" />
                            </span>
                            <span class="text-lg font-bold text-secondary-700">Royal Blue</span>
                        </button>
                        <button type="button"
                                class="inline-flex items-center justify-center rounded-lg border border-[#44b1a0] px-6 py-3 text-base font-medium text-[#44b1a0] transition hover:bg-white">
                            Kies een andere kleur
                        </button>
                    </div>
                </div>
                <div class="mt-4 border border-secondary-100 shadow-lg shadow-black/3">
                    <button type="button" class="flex w-full items-center justify-between bg-white px-5 py-7 text-left"><span
                        class="text-lg font-bold text-secondary-700">Kies je opties</span>
                        <ChevronDownIcon class="h-5 w-5 text-secondary-400 transition" />
                    </button>
                </div>
                <div class="mt-4 border border-secondary-100 shadow-lg shadow-black/3">
                    <button type="button" class="flex w-full items-center justify-between bg-white px-5 py-7 text-left"><span
                        class="text-lg font-bold text-secondary-700">Kies je onderstel</span>
                        <ChevronDownIcon class="h-5 w-5 text-secondary-400 transition" />
                    </button>
                </div>
                <div class="mt-4 border border-secondary-100 shadow-lg shadow-black/3">
                    <button type="button" class="flex w-full items-center justify-between bg-white px-5 py-7 text-left"><span
                        class="text-lg font-bold text-secondary-700">Kies de bediening</span>
                        <ChevronDownIcon class="h-5 w-5 text-secondary-400 transition" />
                    </button>
                </div>
            </div>
            <div class="py-5">
                <p class="text-sm font-bold text-secondary-700" v-text="label('site.product.total-incl-options')"></p>
                <div class="mt-2 flex items-end gap-2" v-if="currentPrice">
                    <span class="text-base font-semibold leading-none text-secondary-500 lg:text-lg line-through" v-if="currentPrice?.from_price_excl" v-text="Money.format(fromPriceLabel)"></span>
                    <span class="text-2xl font-semibold leading-none text-secondary-700 lg:text-4xl" v-text="Money.format(priceLabel)"></span>
                </div>
                <div class="mt-6 flex flex-wrap items-center gap-4">
                    <div class="inline-flex items-center overflow-hidden rounded-full border border-gray-200 bg-white">
                        <button type="button"
                                class="inline-flex h-14 w-10 items-center justify-center border-r border-gray-200 text-secondary-500 transition hover:bg-secondary-50"
                                aria-label="Aantal verlagen">
                            <MinusIcon class="h-4 w-4" />
                        </button>
                        <input type="number" min="1" v-model="amount"
                               class="min-w-[4rem] w-16 border-0 bg-transparent text-center text-sm font-medium text-secondary-700 outline-none ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none">
                        <button type="button"
                                class="inline-flex h-14 w-10 items-center justify-center border-l border-gray-200 text-secondary-500 transition hover:bg-secondary-50"
                                aria-label="Aantal verhogen">
                            <PlusIcon class="h-4 w-4" />
                        </button>
                    </div>
                    <button v-if="currentPrice" type="button" @click="addToCart()"
                            class="inline-flex items-center justify-center rounded-full bg-primary px-8 py-4 text-base font-semibold text-white transition hover:bg-primary-300">
                        {{ label('site.product.add_to_cart') }}
                    </button>
                    <button type="button"
                            class="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white border border-secondary-200/50 text-secondary-400 transition hover:text-red-600"
                            aria-label="Favoriet">
                        <HeartIcon class="h-5 w-5" :fill="product.is_favorite ? 'currentColor' : 'none'" />
                    </button>
                    <AddToPurchaseList
                        v-if="productStore.selectedVariant"
                        :variant="productStore.selectedVariant"
                        class="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white border border-secondary-200/50 text-secondary-400 transition hover:text-secondary-800"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-icon lucide-list">
                            <title>add to purchase list</title>
                            <path d="M3 5h.01"/><path d="M3 12h.01"/><path d="M3 19h.01"/><path d="M8 5h13"/><path d="M8 12h13"/><path d="M8 19h13"/>
                        </svg>
                    </AddToPurchaseList>
                </div>
            </div>
            <div class="border-t border-secondary-200 pt-4">
                <div class="flex items-center gap-3 font-semibold text-primary">
                    <span class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-primary">
                        <CheckIcon class="h-6 w-6 text-primary"/>
                    </span>
                    <span class="text-base">{{ label('site.product.quick-delivery') }}</span>
                </div>
            </div>
        </div>
    </div>

    <div class="flex flex-col px-7 py-5 bg-[#EBF4F1]" v-if="currentVariant?.tieredPrices?.length">
        <div class="text-sm lg:text-base text-black leading-loose" v-for="price in currentVariant.tieredPrices">
            <span v-if="price.quantity > 1">
                {{
                    label('site.product.tiered_price', {
                        'quantity': price.quantity,
                        'price': Money.format(price.price_incl, price.currency),
                        'percentage': Math.round((1 - (price.price_incl / currentVariant.price?.price_incl)) * 100)
                    })
                }}
            </span>
        </div>
    </div>
</template>
